import { privateAxios } from "../lib/api";
import {
  IInventoryDraftResponse,
  InventoryItemDraftType,
} from "../types/draft";
import {
  CatalogInventoryItemType,
  CreateItemType,
  DeleteInventoryItemBatchType,
  IInventoryImportResponse,
  IItemResponse,
  InventoryItemImages,
  ItemType,
  UpdateCatalogCurrencyDto,
  UpdateItemSKUType,
} from "../types/item";
import { ApiConstants } from "../utils/constants";

export class ItemService {
  static async getAllInventoryItems({
    catalogId,
    paging = false,
    limit = 50,
    offset = 0,
    inventoryType,
    searchTerm,
  }: {
    catalogId: string;
    paging?: boolean;
    limit?: number;
    offset?: number;
    inventoryType?: string | null;
    searchTerm?: string | null;
  }): Promise<IItemResponse> {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/catalog/${catalogId}`,
      {
        params: {
          paging,
          limit,
          offset,
          inventoryType,
          searchTerm,
        },
      }
    );

    return response.data as IItemResponse;
  }

  static async getInventoryItems({
    businessId,
    paging = false,
    limit = 50,
    offset = 0,
    inventoryType,
    sortBy = "id",
    sortDirection = "desc",
    searchTerm,
  }: {
    businessId: string;
    paging?: boolean;
    limit?: number;
    offset?: number;
    inventoryType?: string | null;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    searchTerm?: string | null;
  }): Promise<IItemResponse> {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/items/${businessId}`,
      {
        params: {
          paging,
          limit,
          offset,
          inventoryType,
          sortBy,
          sortDirection,
          searchTerm,
        },
      }
    );

    return response.data as IItemResponse;
  }

  static async getInventoryItemsByIds({
    ids,
  }: {
    ids: string[];
  }): Promise<ItemType[]> {
    const response = await privateAxios.get(`${ApiConstants.itemsURL}/by-ids`, {
      params: {
        ids,
      },
    });

    return response.data as ItemType[];
  }

  static async getCatalogInventoryItemsByIds({
    ids,
  }: {
    ids: string[];
  }): Promise<CatalogInventoryItemType[]> {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/catalog/by-ids`,
      {
        params: {
          ids,
        },
      }
    );

    return response.data as CatalogInventoryItemType[];
  }

  static async getInventoryItemsSKU({
    businessId,
  }: {
    businessId: string;
  }): Promise<ItemType[]> {
    const response = await privateAxios.get(`${ApiConstants.itemsURL}/sku`, {
      params: {
        businessId,
      },
    });

    return response.data as ItemType[];
  }

  static async getInventoryItemById({ id }: { id: string }): Promise<ItemType> {
    const response = await privateAxios.get(`${ApiConstants.itemsURL}/${id}`);

    return response.data as ItemType;
  }

  static async verifyItem({
    items,
    catalog,
  }: {
    items: string[];
    catalog: string;
  }): Promise<boolean> {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/verify-item?itemId=${items.join(
        ","
      )}&catalogId=${catalog}`
    );

    return response.data;
  }

  static async createInventoryItem({
    item,
  }: {
    item: CreateItemType;
  }): Promise<{ data: ItemType }> {
    const response = await privateAxios.post(`${ApiConstants.itemsURL}`, item);

    return response.data as { data: ItemType };
  }

  static async createBatchInventoryItems({
    items,
  }: {
    items: CreateItemType[];
  }): Promise<{ data: ItemType[]; duplicates: ItemType[] }> {
    const response = await privateAxios.post(
      `${ApiConstants.itemsURL}/batch`,
      items
    );

    return response.data as { data: ItemType[]; duplicates: ItemType[] };
  }

  static async updateCatalogItems({
    catalogId,
    inventoryItemIds,
    selectAll = false,
    excludedItems = [],
  }: {
    catalogId: string;
    inventoryItemIds: string[];
    selectAll?: boolean;
    excludedItems?: string[];
  }) {
    const payload = { inventoryItemIds, selectAll, excludedItems };
    console.log("PAYLOAD", payload);
    const response = await privateAxios.post(
      `${ApiConstants.itemsURL}/catalog/${catalogId}/batch`,
      payload
    );

    return response.data as CatalogInventoryItemType[];
  }

  static async patchInventoryItem({
    id,
    updateInventoryItemDto,
  }: {
    id: string;
    updateInventoryItemDto: CreateItemType;
  }): Promise<number> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/${id}`,
      updateInventoryItemDto
    );

    return response.data as number;
  }

  static async patchInventoryItemThumbnail({
    id,
    thumbnail,
    images,
  }: {
    id: string;
    thumbnail: string | null;
    images: InventoryItemImages;
  }): Promise<ItemType> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/${id}/thumbnail`,
      {
        thumbnail,
        images,
      }
    );

    return response.data as ItemType;
  }

  static async patchInventoryItemSKU({
    businessId,
    updateSkuDto,
  }: {
    businessId: string;
    updateSkuDto: UpdateItemSKUType[];
  }): Promise<ItemType[]> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/sku`,
      updateSkuDto,
      {
        params: {
          businessId,
        },
      }
    );

    return response.data as ItemType[];
  }

  static async patchInventoryItemQuantity({
    businessId,
    updateQuantityDto,
  }: {
    businessId: string;
    updateQuantityDto: { id: string; quantity: number | null }[];
  }): Promise<number> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/quantity`,
      updateQuantityDto,
      {
        params: {
          businessId,
        },
      }
    );

    return response.data as number;
  }

  static async patchCatalogCurrency({
    catalogId,
    updateCatalogCurrencyDto,
  }: {
    catalogId: string;
    updateCatalogCurrencyDto: UpdateCatalogCurrencyDto;
  }): Promise<CatalogInventoryItemType[]> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/catalog-currency/${catalogId}`,
      updateCatalogCurrencyDto
    );

    return response.data.updatedItems as CatalogInventoryItemType[];
  }

  static async patchInventoryItemPrice({
    updatePriceDto,
  }: {
    updatePriceDto: {
      inventoryId: string;
      price: number;
      // catalogs?: {
      //   catalogInventoryId: string;
      //   price: number;
      // }[];
    }[];
  }): Promise<ItemType[]> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/price/batch`,
      updatePriceDto
    );

    return response.data as ItemType[];
  }

  static async patchCatalogItemPrice({
    updatePriceDto,
    catalogId,
  }: {
    updatePriceDto: {
      catalogInventoryId: string;
      price: number;
      price2: number;
      price3: number;
    }[];
    catalogId: string;
  }): Promise<CatalogInventoryItemType[]> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/catalog/price/batch/${catalogId}`,
      updatePriceDto
    );

    return response.data as CatalogInventoryItemType[];
  }

  // static async getSignedUrlForImageUpload({
  //   id,
  //   filename,
  // }: {
  //   id: string;
  //   filename: string;
  // }): Promise<ItemType> {
  //   const response = await privateAxios.get(
  //     `${ApiConstants.itemsURL}/${id}/signed-url?filename=${filename}`
  //   );

  //   return response.data as ItemType;
  // }

  static async getInventoryItemField({
    fields,
    businessId,
    inventoryType,
    searchTerm,
    isDistinct = true,
  }: {
    fields: string[];
    businessId: string;
    inventoryType?: string;
    searchTerm?: string;
    isDistinct?: boolean;
  }) {
    console.log("FILTERS", {
      businessId,
      fields,
      inventoryType,
      searchTerm,
      isDistinct,
    });
    const response = await privateAxios.get(`${ApiConstants.itemsURL}/fields`, {
      params: {
        businessId,
        fields,
        inventoryType,
        searchTerm,
        isDistinct,
      },
    });
    console.log({ categories: response.data });
    return response.data;
  }

  static async getCatalogInventoryTypes({ catalogId }: { catalogId: string }) {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/catalog-inventory-types`,
      {
        params: {
          catalogId,
        },
      }
    );
    console.log({ categories: response.data });
    return response.data;
  }

  static async getCatalogInventoryItemField({
    fields,
    catalogId,
    inventoryType,
    searchTerm,
    isDistinct = true,
  }: {
    fields: string[];
    catalogId: string;
    inventoryType?: string;
    searchTerm?: string;
    isDistinct?: boolean;
  }) {
    const response = await privateAxios.get(
      `${ApiConstants.itemsURL}/catalog/fields`,
      {
        params: {
          catalogId,
          fields,
          inventoryType,
          searchTerm,
          isDistinct,
        },
      }
    );

    return response.data;
  }

  static async getInventoryItemListByBusinessIdAsSeller(
    sellerId: string | null
  ) {
    return await privateAxios.get(
      `${ApiConstants.itemsURL}/seller/${sellerId}`
    );
  }

  static async getInventoryItemListByBusinessIdAsBuyer(
    buyerId: string,
    sellerId: string
  ) {
    return await privateAxios.get(
      `${ApiConstants.itemsURL}/buyer/${buyerId}/seller/${sellerId}`
    );
  }

  static async deleteInventoryItem({ id }: { id: string }): Promise<number> {
    const response = await privateAxios.delete(
      `${ApiConstants.itemsURL}/${id}`
    );

    return response.data as number;
  }

  static async deleteInventoryItemBatch(
    deleteInventoryItemBatchDto: DeleteInventoryItemBatchType
  ): Promise<number> {
    const response = await privateAxios.patch(
      `${ApiConstants.itemsURL}/delete-batch`,
      deleteInventoryItemBatchDto
    );

    return response.data as number;
  }

  static async exportInventoryItems() {
    try {
      const response = await privateAxios.get(
        `${ApiConstants.itemsURL}/export`,
        {
          responseType: "blob",
        }
      );

      if (!response.data) {
        throw new Error("No data received");
      }

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
          type:
            response.headers["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      const contentDisposition = response.headers["content-disposition"];
      const serverFilename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/["']/g, "")
        : null;
      link.setAttribute(
        "download",
        serverFilename || "Linkz-inventory-template"
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      console.error("Download failed:", error);
      return false;
    }
  }

  /**
   * upload an excel file to backend server
   * @param acceptedFile
   * @returns IInventoryImportDraftResponse
   */
  static async importInventoryItems(
    acceptedFile: File
  ): Promise<{ data: IInventoryImportResponse }> {
    const formData = new FormData();
    formData.append("file", acceptedFile);

    const response = await privateAxios.post(
      `${ApiConstants.itemsURL}/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  }

  /**
   *
   * @param draftId
   * @returns
   */
  static async getInventoryDraft(draftId: string) {
    const response = await privateAxios.get(
      `${ApiConstants.draftURL}/${draftId}`
    );

    return response.data as IInventoryDraftResponse;
  }

  /**
   *
   * @param draftId
   * @param payload
   * @returns
   */
  static async putInventoryDraft(
    draftId: string,
    payload: InventoryItemDraftType[]
  ) {
    const response = await privateAxios.put(
      `${ApiConstants.draftURL}/${draftId}`,
      payload
    );

    return response.data as IInventoryImportResponse;
  }
}
